import {
  calculateDiscount,
  calculateValue,
} from 'checkout/selectors/payment/generic'
import { ExperienceOfferPageState } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import { nonNullable } from 'lib/arrayUtils'
import { SavedExperienceData } from 'tripPlanner/types/tripItem'
import { CreateExperienceRequestBody } from 'tripPlanner/utils/itemConversions/common'

interface Params {
  experience: App.ExperienceOffer
  currentRegionCode: string
  currencyCode: string
  taxExclusivePricing: boolean
  experienceOfferPageState?: ExperienceOfferPageState
  allTicketOptions: App.ExperienceItemTicket[]
  templateId?: string
  templateItemId?: string
}

function hasValidTicketSelections(
  allTicketOptions: App.ExperienceItemTicket[],
  experienceOfferPageState: ExperienceOfferPageState,
) {
  if (!experienceOfferPageState) {
    return false
  }

  const ticketIds = Object.keys(experienceOfferPageState.ticketCounts).filter(
    (id) => experienceOfferPageState.ticketCounts[id] > 0,
  )
  const haveTicketData = (id: string) =>
    allTicketOptions.some((ticket) => ticket.id === id)

  return ticketIds.length > 0 && ticketIds.every(haveTicketData)
}

function hasSomeDataToSave(
  experience: App.ExperienceOffer,
  allTicketOptions: App.ExperienceItemTicket[],
  experienceOfferPageState?: ExperienceOfferPageState,
) {
  if (!experienceOfferPageState) {
    return false // No data to save
  }
  if (experience.hasCalendar) {
    return !!experienceOfferPageState.date // Can at least save the date
  }
  return hasValidTicketSelections(allTicketOptions, experienceOfferPageState) // Non-calendar experience has ticket selections
}

export function createExperienceItemPayload({
  experience,
  currentRegionCode,
  currencyCode,
  taxExclusivePricing,
  experienceOfferPageState,
  allTicketOptions,
  templateId,
  templateItemId,
}: Params): CreateExperienceRequestBody {
  const date = experience.hasCalendar
    ? experienceOfferPageState?.date
    : undefined
  const time = experience.hasTimeslots
    ? experienceOfferPageState?.time
    : undefined

  const location =
    experienceOfferPageState?.redemptionLocation ?? experience.location

  let place: CreateExperienceRequestBody['place'] = {
    type: 'CUSTOM_MULTI_DATES',
    name: location.name,
  }

  const { latitude, longitude } = location

  if (typeof latitude === 'number' && typeof longitude === 'number') {
    place = {
      ...place,
      type: 'GEO_MULTI_DATES',
      point: {
        lat: latitude,
        lng: longitude,
      },
    }
  }

  const payload: CreateExperienceRequestBody = {
    type: 'EXPERIENCE',
    isBooked: false,
    name: experience.name,
    sourceType: 'LE_Experience',
    startDate: date,
    endDate: date,
    startTime: time,
    place,
    code: experience.id,
    savedItemData: {
      regionCode: currentRegionCode,
      currencyCode,
      price: experience.price,
      value: experience.price,
      experienceId: experience.id,
      leExclusive: experience.leExclusive,
      imageIds: nonNullable(experience.images.map((image) => image.id)),
    },
    templateId,
    templateItemId,
  }

  if (
    experienceOfferPageState &&
    hasSomeDataToSave(experience, allTicketOptions, experienceOfferPageState)
  ) {
    // Format deep link data

    let savedItemData: SavedExperienceData = {
      regionCode: currentRegionCode,
      experienceId: experience.id,
      currencyCode,
      date: experienceOfferPageState.date || '',
      languageId: experienceOfferPageState.language?.id,
      time: experienceOfferPageState.time,
      pickupPointId: experienceOfferPageState.pickupPoint?.id,
      redemptionLocationId: experienceOfferPageState.redemptionLocation?.id,
      price: experience.price,
      value: calculateValue(experience.price, experience.discount),
      discountPercent: experience.discount,
      priceDisplayType: taxExclusivePricing
        ? 'taxesAndFeesExclusive'
        : 'taxesAndFeesInclusive',
      leExclusive: experience.leExclusive,
      imageIds: nonNullable(experience.images.map((image) => image.id)),
    }

    if (hasValidTicketSelections(allTicketOptions, experienceOfferPageState)) {
      // Reset price/value so we can add them up properly from the ticket selections
      let price = 0
      let value = 0
      const tickets: SavedExperienceData['tickets'] = []

      Object.entries(experienceOfferPageState.ticketCounts).forEach(
        ([ticketId, count]) => {
          if (count === 0) {
            return
          }

          const ticket = allTicketOptions.find(
            (ticket) => ticket.id === ticketId,
          )

          if (!ticket) {
            throw new Error('Ticket was not in the allTicketOptions array')
          }

          tickets.push({
            ticketId,
            count,
            fareType: ticket.fareType,
            name: ticket.name,
          })

          price += ticket.price * count
          value += calculateValue(ticket.price, ticket.discount) * count
        },
      )

      const discountPercent = calculateDiscount(price, value)

      // This gives us enough data to deep link the user all the way to checkout.
      // So upgrade the deep link data from `experience-bookmark` type to `experience`.
      savedItemData = {
        ...savedItemData,
        price,
        value,
        discountPercent,
        tickets,
      }
    }

    payload.savedItemData = savedItemData
  }

  return payload
}
